"use client";

import { gRPCBaseURL } from "@/src/config";
import {
  ensureValidAccessToken,
  failAuthSession,
  isAuthSessionInvalidated,
  optionalAccessToken,
  refreshAccessToken,
  throwSessionExpiredError,
} from "@/src/util/auth-session";
import { Code, DubboError, Interceptor, StreamRequest, UnaryRequest } from "@apachedubbo/dubbo";
import { createDubboTransport } from "@apachedubbo/dubbo-web";

/** True session/auth failures only — not permission denied (403 / Forbidden). */
const isUnauthorizedError = (err: unknown): boolean => {
  const e = err as { code?: number | string; errorCode?: number; message?: string };
  if (err instanceof DubboError) {
    if (err.code === Code.PermissionDenied) return false;
    if (err.code === Code.Unauthenticated) return true;
  }
  if (typeof e?.code === "number" && e.code === Code.Unauthenticated) return true;
  if (e?.errorCode === 403) return false;
  if (e?.errorCode === 401) return true;
  // "Forbidden" / permission messages must not kill the session.
  if (typeof e?.message === "string") {
    if (/forbidden|permission/i.test(e.message)) return false;
    if (/unauthor|unauthenticated|session.*(expired|outdated)/i.test(e.message)) return true;
  }
  return false;
};

const isUnauthorizedResponse = (res: unknown): boolean => {
  const r = res as {
    errorCode?: number;
    message?: string | { errorCode?: number };
  };
  if (!r) return false;

  const code =
    typeof r.message === "object" && r.message?.errorCode != null
      ? r.message.errorCode
      : r.errorCode;
  // Permission denied is 403 — do not refresh-token / session-expire.
  if (code === 403) return false;
  return code === 401;
};

const PUBLIC_FREIGHT_SALES_RPC =
  "com.onetake.cargogo.air.database.company.TriCompanyFreightSalesService/getPublicFreightSales";

const PublicMethods = new Set([
  "register",
  "verifyRegister",
  "login",
  "resetPassword",
  "verifyResetPassword",
  "getAirports",
  "getAirlines",
  "getCountries",
  "getCountryCodes",
  "getPhoneCodes",
  "getPortOfStations",
  "getSystemOptions",
  "getPublicFreightSales",
  "com.onetake.cargogo.air.database.air.TriAirDatabaseService/getDestinations",
  PUBLIC_FREIGHT_SALES_RPC,
]);

/** Public RPCs that still send a token when the ERP session has one (partner rates). */
const OptionalAuthMethods = new Set(["getPublicFreightSales", PUBLIC_FREIGHT_SALES_RPC]);

const requestMatches = (names: Set<string>, req: UnaryRequest | StreamRequest) => {
  const url = req.url;
  for (const x of names) {
    if (x.includes("/") && url.endsWith(x)) {
      return true;
    }
  }
  const method = req.method.name;
  return !!method && names.has(method);
};

const isPublicRequest = (req: UnaryRequest | StreamRequest) => requestMatches(PublicMethods, req);

const isOptionalAuthRequest = (req: UnaryRequest | StreamRequest) =>
  requestMatches(OptionalAuthMethods, req);

const tryRefreshAndRetry = async (attempt: number, maxRetries: number): Promise<boolean> => {
  if (isAuthSessionInvalidated() || attempt >= maxRetries) {
    return false;
  }
  return refreshAccessToken();
};

const interceptor: Interceptor = (next) => async (req) => {
  req.header.set("x-timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);

  if (isPublicRequest(req)) {
    if (isOptionalAuthRequest(req)) {
      const token = await optionalAccessToken();
      if (token) {
        req.header.set("Authorization", `Bearer ${token}`);
      }
    }
    return next(req);
  }

  if (isAuthSessionInvalidated()) {
    throwSessionExpiredError();
  }

  const maxRetries = 1;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const token = await ensureValidAccessToken();
    req.header.set("Authorization", `Bearer ${token}`);

    try {
      const res = await next(req);
      const unauthorizedByBody = isUnauthorizedResponse(res);
      if (unauthorizedByBody && attempt < maxRetries) {
        if (await tryRefreshAndRetry(attempt, maxRetries)) {
          continue;
        }
        failAuthSession("error.auth.session_expired");
      }
      return res;
    } catch (err) {
      if (isUnauthorizedError(err) && attempt < maxRetries) {
        if (await tryRefreshAndRetry(attempt, maxRetries)) {
          continue;
        }
        failAuthSession("error.auth.session_expired");
      }
      throw err;
    }
  }

  failAuthSession("error.auth.session_expired");
};

const transport = createDubboTransport({
  baseUrl: gRPCBaseURL,
  interceptors: [interceptor],
});

export { refreshAccessToken };

export default transport;
